import Image from 'next/image';
import { appStoreLink, playStoreLink, withCampaignParams } from '@/lib/app-links';
import { cn, getExternalLinkRel } from '@/lib/utils';

interface StoreBadgesProps {
  /** Classes for the wrapper — use this to set alignment. */
  className?: string;
  /**
   * Badge height. Keep it at 44px or more: these are the primary tap target
   * on a phone-sized footer.
   */
  badgeHeightClass?: string;
  /**
   * Full responsive sizing classes for each badge image, overriding
   * `badgeHeightClass` entirely when given. The two official badge assets
   * have different aspect ratios, so sizing both to the same *height*
   * (the default) leaves their right edges uneven; pass a fixed *width*
   * instead (e.g. `"h-auto w-40"`) where they need to line up as a block —
   * stacked in the /app hero, for instance.
   */
  badgeSizeClass?: string;
  /**
   * Marketing tags from the current request, carried onto both links so a
   * badge click from a tagged campaign URL stays attributed to it. The footer
   * omits this; only /app has campaign tags to pass on.
   */
  campaignParams?: URLSearchParams;
}

/**
 * The official Google Play and App Store badges, linked to wherever
 * src/lib/app-links.ts currently points (a OneLink today, the real store
 * listings once we are off it).
 *
 * Shared between the /app landing page and the site footer so the two can
 * never drift apart — a store link that is right in one place and stale in
 * the other is exactly the bug this prevents.
 */
export function StoreBadges({
  className,
  badgeHeightClass = 'h-12',
  badgeSizeClass,
  campaignParams,
}: StoreBadgesProps) {
  const playStoreUrl = withCampaignParams(playStoreLink(), campaignParams);
  const appStoreUrl = withCampaignParams(appStoreLink(), campaignParams);
  const linkClass =
    'inline-flex rounded-lg transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-current';
  const imageClass = badgeSizeClass ?? cn('w-auto', badgeHeightClass);

  return (
    <div className={cn('flex flex-wrap items-center gap-3', className)}>
      <a
        href={playStoreUrl}
        rel={getExternalLinkRel(playStoreUrl, 'noopener noreferrer')}
        aria-label="Get DocuPro on Google Play"
        className={linkClass}
      >
        <Image
          src="/app-download/google-play-badge.png"
          alt="Get it on Google Play"
          width={228}
          height={68}
          className={imageClass}
        />
      </a>
      <a
        href={appStoreUrl}
        rel={getExternalLinkRel(appStoreUrl, 'noopener noreferrer')}
        aria-label="Download DocuPro on the App Store"
        className={linkClass}
      >
        <Image
          src="/app-download/app-store-badge.png"
          alt="Download on the App Store"
          width={213}
          height={72}
          className={imageClass}
        />
      </a>
    </div>
  );
}
